import { useRoute, useRouter } from 'vue-router'
import { ref, computed } from 'vue'
import { defineStore, acceptHMRUpdate } from 'pinia'

import { useAccountStore } from 'src/stores/account'
import { useUtcToday } from 'src/composables/app/system/useUtcToday'

import {
  getRequiresConfirmationApprovals,
  getNextConfirmationDate,
  filterConfirmations,
  getDefaultFilter,
  getDefaultSort,
} from 'src/services/helpers/confirmation.helper'
import { DEFAULT_FILTER_SORT } from 'src/services/consts/confirmations.const'
import {
  getFilterFromQuery,
  getSortAndPaginationFromQuery,
  isSameTableState,
  sanitizeQuery,
  sortItems,
  paginateItems,
} from 'src/services/app/util.service'

export const useConfirmationsStore = defineStore('confirmations', () => {
  const route = useRoute()
  const router = useRouter()
  const accountStore = useAccountStore()
  const utcToday = useUtcToday()

  const filterBy = ref(getFilterFromQuery(getDefaultFilter(), route.query))
  const tableState = ref(getSortAndPaginationFromQuery(getDefaultSort(), route.query))

  // Computed values
  const requiresConfirmationApprovals = computed(() => {
    if (!accountStore?.accountData) return []
    const approvals = accountStore.accountData?.approvals || []
    return getRequiresConfirmationApprovals(approvals, utcToday.value)
  })
  const filteredConfirmations = computed(() => {
    return filterConfirmations(requiresConfirmationApprovals.value, filterBy.value)
  })
  const confirmationsToShow = computed(() => {
    const sorted = sortItems(
      filteredConfirmations.value,
      tableState.value.sortBy,
      tableState.value.descending
    )
    return paginateItems(sorted, tableState.value.page, tableState.value.rowsPerPage)
  })

  const totalRequiringConfirmation = computed(() => requiresConfirmationApprovals.value.length)

  const nextConfirmationDate = computed(() =>
    getNextConfirmationDate(accountStore.accountData?.approvals, utcToday.value)
  )

  const hasActiveFilters = computed(() => {
    return (
      !!filterBy.value.search ||
      filterBy.value.dateCreatedRange !== null ||
      filterBy.value.lastConfirmationRange !== null
    )
  })

  function syncStateFromQuery(query) {
    filterBy.value = getFilterFromQuery(getDefaultFilter(), query)

    if (accountStore.isLoading || accountStore.error) return

    const totalItems = filteredConfirmations.value.length
    const defaultSort = getDefaultSort()
    const nextTableState = getSortAndPaginationFromQuery(defaultSort, query, { totalItems })

    if (!isSameTableState(tableState.value, nextTableState)) {
      tableState.value = nextTableState
    }

    const urlPage = parseInt(query.page) || defaultSort.page
    if (nextTableState.page !== urlPage) {
      const combinedParams = { ...filterBy.value, ...nextTableState }
      const cleanQuery = sanitizeQuery(combinedParams, DEFAULT_FILTER_SORT)
      router.replace({ query: cleanQuery })
    }
  }

  function resetFiltersState() {
    filterBy.value = getDefaultFilter()
    tableState.value = getDefaultSort()
  }

  function updateQuery(fieldsToUpdate) {
    // Updating the query will trigger the watch and update the state
    if (Object.keys(fieldsToUpdate).length === 0) return
    const shouldPushToHistory = fieldsToUpdate.page && fieldsToUpdate.page !== tableState.value.page
    const combinedParams = {
      ...filterBy.value,
      ...tableState.value,
      ...fieldsToUpdate,
    }

    const cleanQuery = sanitizeQuery(combinedParams, DEFAULT_FILTER_SORT)

    const action = shouldPushToHistory ? 'push' : 'replace'
    router[action]({ query: cleanQuery })
  }

  function updateFilters(filterToUpdate) {
    updateQuery({ ...filterToUpdate, page: 1 })
  }

  function updateTableState(stateToUpdate) {
    updateQuery(stateToUpdate)
  }

  function clearFilters() {
    // Clear all filters and reset page
    const newFilter = getDefaultFilter()
    updateQuery({ ...newFilter, page: 1 })
  }

  function resetFilters() {
    // Resets everything
    updateQuery(DEFAULT_FILTER_SORT)
    filterBy.value = getDefaultFilter()
    tableState.value = getDefaultSort()
  }

  return {
    // State
    filterBy,
    tableState,

    // Computed
    requiresConfirmationApprovals,
    filteredConfirmations,
    confirmationsToShow,
    totalRequiringConfirmation,
    nextConfirmationDate,
    hasActiveFilters,

    // Actions
    syncStateFromQuery,
    resetFiltersState,
    updateQuery,
    updateTableState,
    updateFilters,
    clearFilters,
    resetFilters,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useConfirmationsStore, import.meta.hot))
}
